// #region Imports
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import downloadFile from "_shared/_utilities/download-file";
import AHBoolean from "_shared/booleans/boolean";
import Guid from "_shared/guids/guid";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import Document from "documents/document";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/_shared/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { IssueContainer } from "work/proposals/issues/_views/proposal-issue-container";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import SelectedDocumentList from "work/work-agreements/contracts/_views/selected-document-list";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import WorkDocument from "work/work-agreements/contracts/work-document";
import { xor } from "lodash";
// #endregion

// #region Styled Components
const ListContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived: boolean }>(({ waived }) => ({
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "20rem",
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
}));
const DocumentSelectorContainer = styled(ResizableBox)(() => ({
  display: "flex",
  position: "relative"
}));
const SelectedDocs = styled(SelectedDocumentList)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  overflowY: "auto",
  position: "relative",
  flex: 1,
  "& .MuiListItem-root": {
    maxWidth: "550px",
  }
}));
const HeadControls = styled("div")(({ theme }) => ({
  alignItems: "baseline",
  display: "flex",
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  backdropFilter: "blur(5px) saturate(200%)",
  position: "absolute",
  top: 0,
  left: 0,
  margin: "1rem",
  width: "calc(100% - 2rem)",
  height: "calc(100% - 2rem)",
  zIndex: 9999,
}));
// #endregion

type ConflictsUploadProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  proposal?: Proposal;
  commentThreads?: CommentThread[];
  disableEditing?: boolean;
  isSaved: boolean;
  commentsToolTip: string;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
};

export default function ConflictsTab(props: Readonly<ConflictsUploadProps>) {
  const {
    activeReviewField,
    proposalBuilder,
    commentThreads,
    disableEditing,
    commentsToolTip,
    onCommentsClicked,
    onProposalBuilderUpdated,
    isSaved
  } = props;

  // #region State
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] = useState<Guid | null>(null);
  const [isDownloadingFile, setIsDownloadingFile] = useState<Guid | null>(null);

  // #region Hooks
  const confirm = useConfirmDialog();
  const session = useSession();

  // #region Memoized values
  const issues = useMemo(
    () =>
      props.issues?.entries.filter((issue) => {
        const candidates = [
          ProposalField.Conflicts,
          ProposalField.ConflictsDocument(issue.field.id ?? Guid.EMPTY),
          ProposalField.WaiveConflictsCheck,
        ];

        return candidates.some((candidate) => candidate.isEqualTo(issue.field));
      }),
    [props.issues?.entries]
  );

  const hasActiveReviewFieldIssue = useMemo(() => {
    return issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false;
  }, [issues, activeReviewField]);

  const conflictsCheckWaived = useMemo(() => {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflictsCheckWaived)) return false;
    return proposalBuilder.currentSpec.workAgreement.conflictsCheckWaived?.value ?? false;
  }, [proposalBuilder.currentSpec.workAgreement.conflictsCheckWaived]);

  const selectedDocuments = useMemo(() => {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflicts)) return [];
    return proposalBuilder.currentSpec.workAgreement.conflicts ?? [];
  }, [proposalBuilder.currentSpec.workAgreement.conflicts]);

  const selectedDocumentIds = useMemo(
    () => selectedDocuments.map((doc) => doc.id),
    [selectedDocuments]
  );

  const selectedTemplateIds = useMemo(
    () => selectedDocuments.flatMap((doc) => doc.templateIds),
    [selectedDocuments]
  );
  // #endregion

  // #region Functions
  const handleConflictsDocumentRemoved = useCallback(async (documentId: Guid): Promise<void> => {
    const response = await confirm({
      title: "Remove Conflicts Document",
      message: "Are you sure you want to remove this conflicts document?",
    });

    if (
      response === ConfirmResponse.Cancel ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflicts)
    )
      return;

    onProposalBuilderUpdated(
      proposalBuilder.setConflicts(
        (proposalBuilder.currentSpec.workAgreement.conflicts ?? []).filter(
          (doc) => !doc.id.isEqualTo(documentId)
        )
      )
    );
  }, [confirm, onProposalBuilderUpdated, proposalBuilder]);

  const handleDownloadDocumentById = useCallback(
    async (documentId: Guid, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {
      event?.stopPropagation();
      try {
        setIsDownloadingFile(documentId);
        const documentApiService = new DocumentAPIService(session);
        const conflictsDocument = await documentApiService.downloadDocument(documentId);
        await downloadFile(conflictsDocument);
      } catch (err) {
        console.error(err);
        enqueueSnackbar("Failed to download conflicts document. Please try again", { variant: "error" });
      } finally {
        setIsDownloadingFile(null);
      }
    }, [session]);

  const handleDocumentSelectionChanged = useCallback((selectedDocs: Document[], deselectedDocs: Document[]) => {
    if (Masked.isMasked(proposalBuilder.currentSpec?.workAgreement.conflicts)) {
      throw new Error("You do not have permission to view contracts.");
    }

    let existingDocuments = [...(proposalBuilder.currentSpec?.workAgreement.conflicts ?? [])];
    let currentDocuments = [...existingDocuments];

    for (const selectedDoc of selectedDocs) {
      if (!currentDocuments.some((doc) => doc.id.isEqualTo(selectedDoc.id))) {
        currentDocuments.push(
          WorkDocument.fromDocument(
            selectedDoc, 
            WorkDocumentType.Conflicts
          )
        );
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      currentDocuments = currentDocuments.filter((doc) => !doc.id.isEqualTo(deselectedDoc.id));
    }

    // If the document lists are the same, do not trigger an update
    const existingDocIds = existingDocuments.map(d => d.id.value);
    const currentDocIds = currentDocuments.map(d => d.id.value);

    if (xor(existingDocIds, currentDocIds).length === 0) return;

    onProposalBuilderUpdated(proposalBuilder.setConflicts(currentDocuments));
  }, [onProposalBuilderUpdated, proposalBuilder]);

  const handleConflictsDocumentAdded = useCallback((document: Document) => {
    if (Masked.isMasked(proposalBuilder.currentSpec?.workAgreement.conflicts)) {
      throw new Error("You do not have permission to view contracts.");
    }
    
    const updatedDocuments = proposalBuilder.currentSpec.workAgreement.conflicts?.concat(
      WorkDocument.fromDocument(document, WorkDocumentType.Conflicts)
    );

    setCreatingDocumentFromTemplate(null);
    onProposalBuilderUpdated(proposalBuilder.setConflicts(updatedDocuments ?? []));
  }, [onProposalBuilderUpdated, proposalBuilder]);

  const handleBeginCreateDocumentFromTemplate = useCallback(async (templateId: Guid | undefined | null) => {
    if (!templateId) return;
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }, [handleDownloadDocumentById]);

  const handleConflictsCheckWaiverChanged = useCallback(async (checked: AHBoolean) => {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.conflicts)) {
      throw new Error("You do not have permission to view contracts.");
    }
    if (proposalBuilder.currentSpec.workAgreement?.conflicts?.length) {
      const response = await confirm({
        title: "Remove Conflicts Documents?",
        message: "Waiving the conflicts check will remove the documents that are already on the proposal",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    onProposalBuilderUpdated(proposalBuilder.setConflictsCheckWaived(checked).setConflicts([]));
  }, [confirm, onProposalBuilderUpdated, proposalBuilder]);

  // #region Render
  if ((Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflicts)) ||
    (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflictsCheckWaived))) {
    return (
      <Typography variant="body1">
        You do not have permission to view contracts
      </Typography>
    );
  }
  return (
    <>
      <HeadControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer active={hasActiveReviewFieldIssue}>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <FormControlLabel
          control={
            <Switch
              disabled={disableEditing}
              checked={conflictsCheckWaived}
              onChange={(_event, checked) => handleConflictsCheckWaiverChanged(new AHBoolean(checked))}
              color="primary"
            />
          }
          label={
            conflictsCheckWaived ? (
              <strong>Conflicts Check Waived</strong>
            ) : (
              "Conflicts Check Not Waived"
            )
          }
        />
        <CommentsButton
          field={ProposalField.Conflicts}
          isActive={activeReviewField?.isEqualTo(ProposalField.Conflicts)}
          toolTip={commentsToolTip}
          disabled={!isSaved}
          commentThreads={commentThreads}
          onCommentsClicked={
            onCommentsClicked
              ? () => {
                onCommentsClicked?.(ProposalField.Conflicts);
              }
              : undefined
          }
        />
      </HeadControls>
      <ListContainer
        waived={conflictsCheckWaived}
        container
        direction="row"
      >
        {!disableEditing && (
          <>
            <DocumentSelectorContainer
              resizeHandles={["e"]}
              axis='x'
              width={450}
              height={Infinity}
              minConstraints={[375, Infinity]}
            >
              <DocumentSelector
                documentType="conflicts"
                selectedDocumentIds={selectedDocumentIds}
                selectedTemplateIds={selectedTemplateIds}
                onDocumentSelectionChanged={handleDocumentSelectionChanged}
                onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
              />
            </DocumentSelectorContainer>
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          </>
        )}
        <SelectedDocs
          documents={selectedDocuments}
          documentType={WorkDocumentType.Conflicts}
          downloadingFileId={isDownloadingFile}
          disableEditing={disableEditing}
          disableComments={!isSaved}
          commentThreads={commentThreads}
          commentsToolTip={commentsToolTip}
          onCommentsClicked={onCommentsClicked}
          onDownload={handleDownloadDocumentById}
          onDocumentRemoved={handleConflictsDocumentRemoved}
          onDocumentAdded={handleConflictsDocumentAdded}
        />
      </ListContainer>
      {!disableEditing && creatingDocumentFromTemplate && (
        <Uploader
          session={session}
          documentType={WorkDocumentType.Conflicts}
          templateId={creatingDocumentFromTemplate}
          onDocumentUploaded={handleConflictsDocumentAdded}
          onClose={() => setCreatingDocumentFromTemplate(null)}
        />
      )}
    </>
  );
}
